<script setup lang="ts">
import { ref, watch } from 'vue'
import { useDropZone, useFileDialog } from '@vueuse/core'
import { VButton, VIcon } from '@/modules/shared/components'
import { mimeToIconMap } from '@/modules/filing/utils/utils'

///////////////////////////////////////////////////////////////////////////////
// Config
///////////////////////////////////////////////////////////////////////////////

const MAX_FILE_SIZE_HUMAN = '15MB'
const MAX_PAGES = 250

export interface ExistingFile {
  id: string
  name: string
  file_type?: string
}

const props = withDefaults(
  defineProps<{
    accept: string[]
    disabled?: boolean
    existing_files?: ExistingFile[]
    initial_files?: File[]
    multiple_files?: boolean
  }>(),
  {
    accept: () => [],
    disabled: false,
    existing_files: () => [],
    initial_files: () => [],
    multiple_files: true,
  },
)

const emit = defineEmits<{
  (e: 'chosen', files: File[] | File): void
  (e: 'remove:existing-file', fileId: string): void
}>()

///////////////////////////////////////////////////////////////////////////////
// File management
///////////////////////////////////////////////////////////////////////////////

const files = ref<File[]>([...props.initial_files])

const addFile = (file: File) => {
  const is_acceptable = props.accept.length === 0 || props.accept.some((a) => file.name.endsWith(a))
  const is_duplicate = files.value.some((f) => f.name === file.name && f.size === file.size)

  if (is_acceptable && !is_duplicate) {
    if (!props.multiple_files) emptyFiles()
    files.value.push(file)
  }
}

const removeFile = (index: number) => {
  files.value.splice(index, 1)
}

const emptyFiles = () => files.value.splice(0, files.value.length)

watch(files.value, (files) => {
  if (props.multiple_files) {
    emit('chosen', files)
  } else {
    emit('chosen', files[0])
  }
})

///////////////////////////////////////////////////////////////////////////////
// Drag zone
///////////////////////////////////////////////////////////////////////////////

const drop_zone_ref = ref<HTMLDivElement>()

const onDrop = (dz_files: File[] | null) => {
  if (dz_files === null) return
  dz_files.forEach(addFile)
}

const { isOverDropZone } = useDropZone(drop_zone_ref, onDrop)

///////////////////////////////////////////////////////////////////////////////
// File dialog
///////////////////////////////////////////////////////////////////////////////

const {
  files: fd_files,
  open,
  reset,
} = useFileDialog({ accept: props.accept.join(','), multiple: props.multiple_files })

watch(fd_files, (fileList) => {
  const fileArray = Array.from(fileList) as File[]
  fileArray.forEach(addFile)
})

///////////////////////////////////////////////////////////////////////////////
// Formatting
///////////////////////////////////////////////////////////////////////////////

const format_extension = (extension: string) => extension.substring(1).toUpperCase()

const formatted_accept = props.accept.map(format_extension).join(', ')

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const fileIcon = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase()
  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    csv: 'text/csv',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
  }
  const mime = mimeMap[ext ?? ''] ?? 'application/octet-stream'
  return mimeToIconMap[mime] ?? 'EmptyTypeDefault'
}
</script>
<template>
  <button
    class="group block w-full rounded-md bg-gradient-to-br from-white via-white to-gray-100 p-2"
    :class="[disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer']"
    :disabled="disabled"
    ref="drop_zone_ref"
    type="button"
    @click="open"
  >
    <div
      class="rounded-lg border-2 border-dashed p-10 transition-colors"
      :class="[isOverDropZone ? 'border-[#3b88af]/20' : 'border-gray-200']"
    >
      <div class="flex items-center justify-center">
        <div class="relative h-12 w-12">
          <VIcon
            name="ZipTypeDefault"
            size="48"
            class="absolute z-20 block transition-transform duration-150 ease-in-out group-hover:scale-110"
            :class="{ 'scale-110': isOverDropZone }"
          />
          <VIcon
            name="EmptyTypeDefault"
            size="48"
            class="absolute -left-1.5 top-1 block -rotate-6 opacity-40 transition-transform duration-150 ease-in-out group-hover:scale-100"
            :class="{ 'scale-100': isOverDropZone, 'scale-90': !isOverDropZone }"
          />
          <VIcon
            name="EmptyTypeDefault"
            size="48"
            class="absolute -right-3.5 top-1 block rotate-12 opacity-50 transition-transform duration-150 ease-in-out group-hover:scale-100"
            :class="{ 'scale-100': isOverDropZone, 'scale-90': !isOverDropZone }"
          />
        </div>
      </div>
      <p class="mt-3 text-center text-gray-600">Drop files here or <span class="underline">browse</span></p>
      <p class="mt-1 text-center text-xs text-gray-400">
        <template v-if="formatted_accept">{{ formatted_accept }} — </template>&#60;{{ MAX_FILE_SIZE_HUMAN }} and &#60;{{
          MAX_PAGES
        }}
        pages
      </p>
    </div>
  </button>

  <div v-show="existing_files.length || files.length" class="mt-4">
    <ul class="rounded-md text-sm text-gray-600 ring-1 ring-gray-700/20">
      <li
        v-for="file in existing_files"
        :key="file.id"
        class="flex items-center space-x-2 border-b border-gray-100 px-3 py-2 last:border-none"
      >
        <VIcon
          :name="file.file_type ? (mimeToIconMap[file.file_type] ?? 'EmptyTypeDefault') : fileIcon(file.name)"
          size="20"
          class="shrink-0"
        />
        <div class="flex-1 truncate text-sm">{{ file.name }}</div>
        <VButton size="xs-narrow" variant="invisible" @click="emit('remove:existing-file', file.id)">
          <VIcon name="X" />
        </VButton>
      </li>
      <li
        v-for="(file, index) in files"
        :key="file.name"
        class="flex items-center space-x-2 border-b border-gray-100 px-3 py-2 last:border-none"
      >
        <VIcon :name="fileIcon(file.name)" size="20" class="shrink-0" />
        <div class="flex-1 truncate text-sm">{{ file.name }}</div>
        <span class="shrink-0 text-xs text-gray-400">{{ formatFileSize(file.size) }}</span>
        <VButton @click="removeFile(index)" :disabled="disabled" size="xs-narrow" variant="invisible">
          <VIcon name="X" />
        </VButton>
      </li>
    </ul>
  </div>
</template>
